import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, CheckCircle, Shield, Award, FileCheck, Gauge, ClipboardCheck, Truck, Flame, Zap, Search, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal } from '../hooks/useReveal';

// REAL factory photos
const IMG_INCOMING = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/kky4w1q7_WhatsApp%20Image%202026-05-30%20at%2013.55.10.jpeg';
const IMG_CORE = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/i2cqt4qd_WhatsApp%20Image%202026-05-30%20at%2014.21.36.jpeg';
const IMG_COIL = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/ocsy1269_WhatsApp%20Image%202026-05-30%20at%2013.43.51.jpeg';
const IMG_ASSEMBLY = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const IMG_VACUUM = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gfe4xo80_WhatsApp%20Image%202024-11-17%20at%2021.04.49%20%281%29.jpeg';
const IMG_TESTING = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/20ni6nuw_WhatsApp%20Image%202026-05-30%20at%2014.17.04.jpeg';
const IMG_FINAL = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';

const qualityContent = {
  tr: {
    pageTitle: 'Kalite Guvence Surecimiz',
    pageSubtitle: 'Her trafomuz IEC 60076, ISO 9001 ve musteri teknik sartnamelerine uygun olarak uretilir. Uretimin her asamasinda kalite kontrol uygulanir ve tum sonuclar kayit altina alinir.',
    stepLabel: 'Asama',
    checkpoints: 'Kontrol Noktasi',
    statsSteps: 'Kontrol Asamasi',
    statsPoints: 'Kontrol Noktasi',
    statsStandard: 'Standart',
    certsTitle: 'Sertifikalar ve Standartlar',
    certsSubtitle: 'Standartlar',
    ctaText: 'Sertifikalarin guncel versiyonlari icin bizimle iletisime gecin',
    ctaButton: 'Iletisime Gecin',
    steps: [
      { title: 'Gelen Malzeme Kontrolu', purpose: 'Uretime girecek tum malzemelerin teknik sartnameye uygunlugunu dogrulamak.', checks: ['Silisli sac kalite kontrolu', 'Bakir / Aluminyum iletken kontrolu', 'Izolasyon malzemeleri kontrolu', 'Sertifika ve malzeme uygunluk dogrulamasi', 'Boyutsal olcum ve gorsel muayene'] },
      { title: 'Nuve Imalati ve Kontrolu', purpose: 'Dusuk kayip ve dusuk gurultulu nuve elde etmek.', checks: ['Kesim dogrulugu', 'Istifleme kalitesi', 'Nuve sikiligi', 'Manyetik devre kontrolu', 'Bosta kayip dogrulamasi'] },
      { title: 'Bobin Sarim ve Izolasyon Kontrolu', purpose: 'Elektriksel ve mekanik dayanimi yuksek sargilar uretmek.', checks: ['Sarim olculeri', 'Iletken kesiti dogrulamasi', 'Katman izolasyonlari', 'Presleme islemleri', 'Direnc olcumleri'] },
      { title: 'Aktif Kisim Montaji', purpose: 'Kisa devre kuvvetlerine dayanikli aktif kisim olusturmak.', checks: ['Nuve-bobin merkezleme', 'Izolasyon mesafeleri', 'Kademe degistirici baglantilari', 'Mekanik sikilk kontrolleri'] },
      { title: 'Vakum Kurutma ve Yag Dolumu', purpose: 'Izolasyon sisteminin maksimum omurle calismasini saglamak.', checks: ['Nem seviyesi olcumu', 'Vakum seviyesi takibi', 'Yag dielektrik dayanimi', 'Yag dolum parametreleri'] },
      { title: 'Rutin ve Tip Testleri', purpose: 'IEC 60076 standardina uygun test sureclerini tamamlamak.', checks: ['Sarim direnci & Donusum orani (TTR)', 'Bosta kayip ve akim', 'Yukte kayip & Kisa devre empedansi', 'Izolasyon testleri', 'Uygulanan gerilim testi', 'Enduklenen gerilim testi'] },
      { title: 'Son Kontrol ve Sevkiyat', purpose: 'Trafonun musteriye guvenli ve eksiksiz ulasmasini saglamak.', checks: ['Isimlik dogrulamasi', 'Boya ve kaplama kontrolu', 'Aksesuar kontrolu', 'Kacak kontrolu', 'Sevkiyat guvenligi'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'Kalite Yonetim Sistemi' },
      { title: 'IEC 60076', desc: 'Uygun Uretim' },
      { title: '%100 Rutin Test', desc: 'Uygulamasi' },
      { title: 'Kalibrasyonlu', desc: 'Olcum Cihazlari' },
      { title: 'Izlenebilir', desc: 'Uretim Kayitlari' },
      { title: 'FAT', desc: 'Fabrika Kabul Testi' },
    ],
  },
  en: {
    pageTitle: 'Our Quality Assurance Process',
    pageSubtitle: 'Every transformer we produce complies with IEC 60076, ISO 9001 and customer technical specifications. Quality control is applied at every stage of production and all results are recorded.',
    stepLabel: 'Stage',
    checkpoints: 'Checkpoints',
    statsSteps: 'Control Stages',
    statsPoints: 'Checkpoints',
    statsStandard: 'Standard',
    certsTitle: 'Certifications & Standards',
    certsSubtitle: 'Standards',
    ctaText: 'Contact us for updated versions of certificates',
    ctaButton: 'Get In Touch',
    steps: [
      { title: 'Incoming Material Control', purpose: 'To verify compliance of all materials entering production with technical specifications.', checks: ['Silicon steel sheet quality control', 'Copper / Aluminum conductor control', 'Insulation materials control', 'Certificate and material conformity verification', 'Dimensional measurement and visual inspection'] },
      { title: 'Core Manufacturing & Control', purpose: 'To achieve low-loss and low-noise core.', checks: ['Cutting accuracy', 'Stacking quality', 'Core tightness', 'Magnetic circuit control', 'No-load loss verification'] },
      { title: 'Coil Winding & Insulation Control', purpose: 'To produce windings with high electrical and mechanical strength.', checks: ['Winding dimensions', 'Conductor cross-section verification', 'Layer insulations', 'Pressing operations', 'Resistance measurements'] },
      { title: 'Active Part Assembly', purpose: 'To create an active part resistant to short-circuit forces.', checks: ['Core-coil centering', 'Insulation clearances', 'Tap changer connections', 'Mechanical tightness controls'] },
      { title: 'Vacuum Drying & Oil Filling', purpose: 'To ensure the insulation system operates with maximum lifespan.', checks: ['Moisture level measurement', 'Vacuum level monitoring', 'Oil dielectric strength', 'Oil filling parameters'] },
      { title: 'Routine & Type Tests', purpose: 'To complete test processes in accordance with IEC 60076 standard.', checks: ['Winding resistance & Turns ratio (TTR)', 'No-load loss and current', 'Load loss & Short-circuit impedance', 'Insulation tests', 'Applied voltage test', 'Induced voltage test'] },
      { title: 'Final Inspection & Shipping', purpose: 'To ensure the transformer reaches the customer safely and completely.', checks: ['Nameplate verification', 'Paint and coating control', 'Accessory control', 'Leak control', 'Shipping safety'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'Quality Management System' },
      { title: 'IEC 60076', desc: 'Compliant Production' },
      { title: '100% Routine Test', desc: 'Application' },
      { title: 'Calibrated', desc: 'Measuring Equipment' },
      { title: 'Traceable', desc: 'Production Records' },
      { title: 'FAT', desc: 'Factory Acceptance Test' },
    ],
  },
  fr: {
    pageTitle: 'Notre Processus d\'Assurance Qualite',
    pageSubtitle: 'Chaque transformateur que nous produisons est conforme aux normes IEC 60076, ISO 9001 et aux specifications techniques du client.',
    stepLabel: 'Etape',
    checkpoints: 'Points de Controle',
    statsSteps: 'Etapes de Controle',
    statsPoints: 'Points de Controle',
    statsStandard: 'Norme',
    certsTitle: 'Certifications et Normes',
    certsSubtitle: 'Normes',
    ctaText: 'Contactez-nous pour les versions actualisees des certificats',
    ctaButton: 'Contactez-Nous',
    steps: [
      { title: 'Controle des Materiaux Entrants', purpose: 'Verifier la conformite de tous les materiaux.', checks: ['Controle des toles silicium', 'Controle conducteurs cuivre/aluminium', 'Controle materiaux isolants', 'Verification certificats', 'Mesures dimensionnelles'] },
      { title: 'Fabrication et Controle du Noyau', purpose: 'Obtenir un noyau a faibles pertes et faible bruit.', checks: ['Precision de coupe', 'Qualite d\'empilage', 'Serrage du noyau', 'Controle circuit magnetique', 'Verification pertes a vide'] },
      { title: 'Bobinage et Controle d\'Isolation', purpose: 'Produire des enroulements a haute resistance.', checks: ['Dimensions de bobinage', 'Verification section conducteur', 'Isolations entre couches', 'Operations de pressage', 'Mesures de resistance'] },
      { title: 'Assemblage Partie Active', purpose: 'Creer une partie active resistante aux courts-circuits.', checks: ['Centrage noyau-bobine', 'Distances d\'isolation', 'Connexions changeur de prises', 'Controles de serrage'] },
      { title: 'Sechage sous Vide et Remplissage d\'Huile', purpose: 'Assurer la duree de vie maximale du systeme d\'isolation.', checks: ['Mesure du taux d\'humidite', 'Suivi du niveau de vide', 'Rigidite dielectrique de l\'huile', 'Parametres de remplissage'] },
      { title: 'Tests de Routine et de Type', purpose: 'Completer les tests selon la norme IEC 60076.', checks: ['Resistance d\'enroulement & Rapport de transformation', 'Pertes et courant a vide', 'Pertes en charge & Impedance de court-circuit', 'Tests d\'isolation', 'Test de tension appliquee', 'Test de tension induite'] },
      { title: 'Controle Final et Expedition', purpose: 'Assurer la livraison sure et complete au client.', checks: ['Verification plaque signaletique', 'Controle peinture et revetement', 'Controle accessoires', 'Controle de fuite', 'Securite d\'expedition'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'Systeme de Management Qualite' },
      { title: 'IEC 60076', desc: 'Production Conforme' },
      { title: '100% Tests', desc: 'de Routine' },
      { title: 'Calibre', desc: 'Equipement de Mesure' },
      { title: 'Tracable', desc: 'Registres de Production' },
      { title: 'FAT', desc: 'Test d\'Acceptation Usine' },
    ],
  },
  es: {
    pageTitle: 'Nuestro Proceso de Aseguramiento de Calidad',
    pageSubtitle: 'Cada transformador que producimos cumple con IEC 60076, ISO 9001 y las especificaciones tecnicas del cliente.',
    stepLabel: 'Etapa',
    checkpoints: 'Puntos de Control',
    statsSteps: 'Etapas de Control',
    statsPoints: 'Puntos de Control',
    statsStandard: 'Estandar',
    certsTitle: 'Certificaciones y Estandares',
    certsSubtitle: 'Estandares',
    ctaText: 'Contactenos para versiones actualizadas de los certificados',
    ctaButton: 'Contactenos',
    steps: [
      { title: 'Control de Material Entrante', purpose: 'Verificar la conformidad de todos los materiales.', checks: ['Control de laminas de silicio', 'Control de conductores cobre/aluminio', 'Control de materiales aislantes', 'Verificacion de certificados', 'Medicion dimensional'] },
      { title: 'Fabricacion y Control del Nucleo', purpose: 'Obtener un nucleo de bajas perdidas y bajo ruido.', checks: ['Precision de corte', 'Calidad de apilamiento', 'Ajuste del nucleo', 'Control del circuito magnetico', 'Verificacion de perdidas en vacio'] },
      { title: 'Bobinado y Control de Aislamiento', purpose: 'Producir devanados de alta resistencia.', checks: ['Dimensiones de bobinado', 'Verificacion de seccion del conductor', 'Aislamientos entre capas', 'Operaciones de prensado', 'Mediciones de resistencia'] },
      { title: 'Montaje de Parte Activa', purpose: 'Crear una parte activa resistente a cortocircuitos.', checks: ['Centrado nucleo-bobina', 'Distancias de aislamiento', 'Conexiones del cambiador de tomas', 'Controles de ajuste mecanico'] },
      { title: 'Secado al Vacio y Llenado de Aceite', purpose: 'Asegurar la vida util maxima del sistema de aislamiento.', checks: ['Medicion del nivel de humedad', 'Seguimiento del nivel de vacio', 'Rigidez dielectrica del aceite', 'Parametros de llenado'] },
      { title: 'Pruebas de Rutina y Tipo', purpose: 'Completar pruebas segun norma IEC 60076.', checks: ['Resistencia de devanado y relacion de transformacion', 'Perdidas y corriente en vacio', 'Perdidas en carga e impedancia de cortocircuito', 'Pruebas de aislamiento', 'Prueba de tension aplicada', 'Prueba de tension inducida'] },
      { title: 'Inspeccion Final y Envio', purpose: 'Asegurar la entrega segura y completa al cliente.', checks: ['Verificacion de placa de caracteristicas', 'Control de pintura y recubrimiento', 'Control de accesorios', 'Control de fugas', 'Seguridad de envio'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'Sistema de Gestion de Calidad' },
      { title: 'IEC 60076', desc: 'Produccion Conforme' },
      { title: '100% Pruebas', desc: 'de Rutina' },
      { title: 'Calibrado', desc: 'Equipos de Medicion' },
      { title: 'Trazable', desc: 'Registros de Produccion' },
      { title: 'FAT', desc: 'Prueba de Aceptacion en Fabrica' },
    ],
  },
  ar: {
    pageTitle: 'عملية ضمان الجودة لدينا',
    pageSubtitle: 'كل محول ننتجه يتوافق مع IEC 60076 و ISO 9001 والمواصفات الفنية للعميل. يتم تطبيق مراقبة الجودة في كل مرحلة من مراحل الانتاج.',
    stepLabel: 'مرحلة',
    checkpoints: 'نقاط تفتيش',
    statsSteps: 'مراحل الرقابة',
    statsPoints: 'نقاط التفتيش',
    statsStandard: 'المعيار',
    certsTitle: 'الشهادات والمعايير',
    certsSubtitle: 'المعايير',
    ctaText: 'تواصل معنا للحصول على النسخ المحدثة من الشهادات',
    ctaButton: 'تواصل معنا',
    steps: [
      { title: 'فحص المواد الواردة', purpose: 'التحقق من مطابقة جميع المواد للمواصفات الفنية.', checks: ['فحص صفائح السيليكون', 'فحص موصلات النحاس/الالمنيوم', 'فحص مواد العزل', 'التحقق من الشهادات', 'القياس البعدي والفحص البصري'] },
      { title: 'تصنيع ومراقبة القلب', purpose: 'الحصول على قلب منخفض الفقد والضوضاء.', checks: ['دقة القطع', 'جودة التكديس', 'احكام القلب', 'فحص الدائرة المغناطيسية', 'التحقق من فقد اللاحمل'] },
      { title: 'لف الملفات ومراقبة العزل', purpose: 'انتاج ملفات ذات متانة كهربائية وميكانيكية عالية.', checks: ['ابعاد اللف', 'التحقق من مقطع الموصل', 'عزل الطبقات', 'عمليات الضغط', 'قياسات المقاومة'] },
      { title: 'تجميع الجزء الفعال', purpose: 'انشاء جزء فعال مقاوم لقوى القصر.', checks: ['محاذاة القلب والملف', 'مسافات العزل', 'وصلات مغير النقر', 'فحوصات الاحكام الميكانيكي'] },
      { title: 'التجفيف بالتفريغ وتعبئة الزيت', purpose: 'ضمان عمر اقصى لنظام العزل.', checks: ['قياس مستوى الرطوبة', 'مراقبة مستوى التفريغ', 'قوة عزل الزيت الكهربائية', 'معلمات التعبئة'] },
      { title: 'الاختبارات الروتينية والنمطية', purpose: 'اتمام الاختبارات وفق معيار IEC 60076.', checks: ['مقاومة الملف ونسبة التحويل', 'فقد وتيار اللاحمل', 'فقد الحمل ومعاوقة القصر', 'اختبارات العزل', 'اختبار الجهد المطبق', 'اختبار الجهد المستحث'] },
      { title: 'الفحص النهائي والشحن', purpose: 'ضمان وصول المحول بامان وبشكل كامل للعميل.', checks: ['التحقق من لوحة البيانات', 'فحص الطلاء والتغليف', 'فحص الملحقات', 'فحص التسريب', 'سلامة الشحن'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'نظام ادارة الجودة' },
      { title: 'IEC 60076', desc: 'انتاج متوافق' },
      { title: '100% اختبارات', desc: 'روتينية' },
      { title: 'معاير', desc: 'اجهزة قياس' },
      { title: 'قابل للتتبع', desc: 'سجلات الانتاج' },
      { title: 'FAT', desc: 'اختبار القبول المصنعي' },
    ],
  },
  fa: {
    pageTitle: 'فرآیند تضمین کیفیت ما',
    pageSubtitle: 'هر ترانسفورمری که تولید می کنیم مطابق با IEC 60076 و ISO 9001 و مشخصات فنی مشتری است.',
    stepLabel: 'مرحله',
    checkpoints: 'نقاط بازرسی',
    statsSteps: 'مراحل کنترل',
    statsPoints: 'نقاط بازرسی',
    statsStandard: 'استاندارد',
    certsTitle: 'گواهینامه ها و استانداردها',
    certsSubtitle: 'استانداردها',
    ctaText: 'برای نسخه های به روز گواهینامه ها با ما تماس بگیرید',
    ctaButton: 'تماس بگیرید',
    steps: [
      { title: 'کنترل مواد ورودی', purpose: 'تایید انطباق تمام مواد با مشخصات فنی.', checks: ['کنترل کیفیت ورق سیلیکون', 'کنترل هادی مس/آلومینیوم', 'کنترل مواد عایقی', 'تایید گواهینامه و مواد', 'اندازه گیری ابعادی'] },
      { title: 'ساخت و کنترل هسته', purpose: 'دستیابی به هسته کم تلفات و کم صدا.', checks: ['دقت برش', 'کیفیت چیدمان', 'محکمی هسته', 'کنترل مدار مغناطیسی', 'تایید تلفات بی بار'] },
      { title: 'سیم پیچی و کنترل عایق', purpose: 'تولید سیم پیچ های با استحکام بالا.', checks: ['ابعاد سیم پیچی', 'تایید سطح مقطع هادی', 'عایق لایه ها', 'عملیات پرس', 'اندازه گیری مقاومت'] },
      { title: 'مونتاژ قسمت فعال', purpose: 'ایجاد قسمت فعال مقاوم در برابر اتصال کوتاه.', checks: ['مرکزیت هسته-سیم پیچ', 'فواصل عایقی', 'اتصالات تپ چنجر', 'کنترل محکمی مکانیکی'] },
      { title: 'خشک کردن خلا و پر کردن روغن', purpose: 'اطمینان از حداکثر عمر سیستم عایق.', checks: ['اندازه گیری رطوبت', 'پایش سطح خلا', 'استقامت دی الکتریک روغن', 'پارامترهای پر کردن'] },
      { title: 'آزمایش های روتین و نوعی', purpose: 'تکمیل آزمایش ها طبق استاندارد IEC 60076.', checks: ['مقاومت سیم پیچ و نسبت تبدیل', 'تلفات و جریان بی بار', 'تلفات بار و امپدانس اتصال کوتاه', 'آزمایش های عایقی', 'آزمایش ولتاژ اعمالی', 'آزمایش ولتاژ القایی'] },
      { title: 'بازرسی نهایی و ارسال', purpose: 'اطمینان از رسیدن ایمن و کامل ترانسفورمر به مشتری.', checks: ['تایید پلاک مشخصات', 'کنترل رنگ و پوشش', 'کنترل لوازم جانبی', 'کنترل نشتی', 'ایمنی حمل و نقل'] },
    ],
    certs: [
      { title: 'ISO 9001', desc: 'سیستم مدیریت کیفیت' },
      { title: 'IEC 60076', desc: 'تولید منطبق' },
      { title: '100% آزمایش', desc: 'روتین' },
      { title: 'کالیبره', desc: 'تجهیزات اندازه گیری' },
      { title: 'قابل ردیابی', desc: 'سوابق تولید' },
      { title: 'FAT', desc: 'آزمایش پذیرش کارخانه' },
    ],
  },
};

const stepImages = [IMG_INCOMING, IMG_CORE, IMG_COIL, IMG_ASSEMBLY, IMG_VACUUM, IMG_TESTING, IMG_FINAL];
const stepIcons = [
  <Search className="w-5 h-5" />,
  <Flame className="w-5 h-5" />,
  <Gauge className="w-5 h-5" />,
  <ClipboardCheck className="w-5 h-5" />,
  <FlaskConical className="w-5 h-5" />,
  <Zap className="w-5 h-5" />,
  <Truck className="w-5 h-5" />,
];
const stepCheckCounts = [8, 7, 7, 7, 4, 7, 7];
const certIcons = [
  <Shield className="w-6 h-6" />,
  <Award className="w-6 h-6" />,
  <FileCheck className="w-6 h-6" />,
  <Gauge className="w-6 h-6" />,
  <ClipboardCheck className="w-6 h-6" />,
  <Search className="w-6 h-6" />,
];

function QualityStep({ num, title, purpose, checks, checkCount, img, icon, reverse, labels }) {
  return (
    <Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-stretch py-14 md:py-20 border-b border-white/5">
        <div className={`relative overflow-hidden rounded-sm group ${reverse ? 'lg:order-2' : ''}`}>
          <img src={img} alt={title} className="w-full h-[320px] md:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05060A]/60 via-transparent to-transparent" />
          <div className="absolute top-5 left-5">
            <div className="w-10 h-10 rounded-sm bg-blue-600/90 flex items-center justify-center text-white font-bold text-sm backdrop-blur-sm">{num}</div>
          </div>
          <div className="absolute bottom-5 left-5">
            <span className="inline-block bg-blue-600/20 backdrop-blur-md border border-blue-400/20 text-blue-300 text-xs font-bold px-3 py-1.5 rounded-sm uppercase tracking-wider">
              {checkCount} {labels.checkpoints}
            </span>
          </div>
        </div>
        <div className={`flex flex-col justify-center ${reverse ? 'lg:order-1' : ''}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400">{icon}</div>
            <span className="text-xs text-blue-400 uppercase tracking-[0.2em] font-bold">{labels.stepLabel} {num}</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-3">{title}</h3>
          <p className="text-sm text-blue-400/80 italic mb-5 border-l-2 border-blue-600/40 pl-4">{purpose}</p>
          <ul className="space-y-2.5">
            {checks.map((check, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <span>{check}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
}

export default function Quality() {
  const { language } = useLanguage();
  const content = qualityContent[language] || qualityContent.en;

  return (
    <div data-testid="quality-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt Transformer</p>
          <h1 data-testid="quality-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">{content.pageTitle}</h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">{content.pageSubtitle}</p>
          <div className="flex items-center gap-8 mt-10">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">7</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{content.statsSteps}</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">47+</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{content.statsPoints}</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-400">IEC</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">60076 {content.statsStandard}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-10 md:py-16 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {content.steps.map((step, i) => (
            <QualityStep
              key={i}
              num={`0${i + 1}`}
              title={step.title}
              purpose={step.purpose}
              checks={step.checks}
              checkCount={stepCheckCounts[i]}
              img={stepImages[i]}
              icon={stepIcons[i]}
              reverse={i % 2 === 1}
              labels={content}
            />
          ))}
        </div>
      </section>

      {/* Certs */}
      <section data-testid="certifications-grid" className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-16">
            <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-4">{content.certsSubtitle}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{content.certsTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {content.certs.map((card, i) => (
              <Reveal key={card.title}>
                <div className="border border-white/10 bg-[#0B0E14] p-6 text-center hover:border-blue-600/30 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-sm bg-blue-600/10 flex items-center justify-center text-blue-400 mb-4 mx-auto group-hover:bg-blue-600/20 transition-colors">{certIcons[i]}</div>
                  <h3 className="text-sm font-bold text-white mb-0.5">{card.title}</h3>
                  <p className="text-xs text-slate-500">{card.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="py-12 bg-[#0B0E14] border-y border-white/5 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-center gap-20 px-10">
              {['ISO 9001', 'ISO 14001', 'ISO 45001', 'IEC 60076', 'KEMA', 'TSE', 'CE', '100% ROUTINE TEST', 'FAT'].map((c) => (
                <span key={`${idx}-${c}`} className="text-xl font-bold text-white/8 tracking-widest uppercase">{c}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-slate-400 mb-4">{content.ctaText}</p>
            <Link to="/contact">
              <Button data-testid="quality-contact-cta" className="bg-blue-600 text-white rounded-sm px-10 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                {content.ctaButton} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
